import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Form,
  Input,
  Message,
  Modal,
  Space,
  Switch,
  Table,
} from '@arco-design/web-react'
import {
  createCustomOAuthProviderApi,
  deleteCustomOAuthProviderApi,
  listCustomOAuthProvidersApi,
  updateCustomOAuthProviderApi,
  type CustomOAuthProvider,
} from '@/api/customOAuth'
import SensitiveInput from '../SensitiveInput'
import SettingSectionCard from '../SettingSectionCard'
import type { OptionsAPI } from '../useOptions'

const emptyForm = (): Partial<CustomOAuthProvider> => ({
  name: '',
  slug: '',
  enabled: true,
  client_id: '',
  client_secret: '',
  authorization_endpoint: '',
  token_endpoint: '',
  user_info_endpoint: '',
  scopes: 'openid profile email',
  user_id_field: 'sub',
  username_field: 'preferred_username',
  display_name_field: 'name',
  email_field: 'email',
})

export default function CustomOAuthProvidersSection({ options }: { options: OptionsAPI }) {
  const [items, setItems] = useState<CustomOAuthProvider[]>([])
  const [loading, setLoading] = useState(false)
  const [visible, setVisible] = useState(false)
  const [editing, setEditing] = useState<CustomOAuthProvider | null>(null)
  const [form, setForm] = useState<Partial<CustomOAuthProvider>>(emptyForm())

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await listCustomOAuthProvidersApi()
      setItems(data ?? [])
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '加载自定义 OAuth 失败')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm())
    setVisible(true)
  }

  const openEdit = (row: CustomOAuthProvider) => {
    setEditing(row)
    setForm({ ...row, client_secret: '' })
    setVisible(true)
  }

  const submit = async () => {
    if (!form.name?.trim() || !form.slug?.trim() || !form.client_id?.trim()) {
      Message.error('请填写名称、Slug 与 Client ID')
      return
    }
    try {
      const payload = { ...form }
      if (!payload.client_secret?.trim()) delete payload.client_secret
      if (editing) {
        await updateCustomOAuthProviderApi(editing.id, payload)
      } else {
        await createCustomOAuthProviderApi(payload)
      }
      Message.success('已保存')
      setVisible(false)
      await load()
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '保存失败')
    }
  }

  const remove = async (id: number) => {
    try {
      await deleteCustomOAuthProviderApi(id)
      Message.success('已删除')
      await load()
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '删除失败')
    }
  }

  return (
    <SettingSectionCard
      title="自定义 OAuth 提供商"
      subtitle="独立数据库表 CRUD，与内置 OAuth 不同"
      keys={[]}
      options={options}
      hideSave
    >
      <Space className="mb-3">
        <Button type="primary" onClick={openCreate}>
          添加提供商
        </Button>
        <Button onClick={load} loading={loading}>
          刷新
        </Button>
      </Space>
      <Table
        loading={loading}
        rowKey="id"
        pagination={false}
        columns={[
          { title: '名称', dataIndex: 'name' },
          { title: 'Slug', dataIndex: 'slug' },
          {
            title: '启用',
            dataIndex: 'enabled',
            render: (v: boolean) => (v ? '是' : '否'),
          },
          {
            title: '操作',
            render: (_: unknown, row: CustomOAuthProvider) => (
              <Space>
                <Button size="mini" onClick={() => openEdit(row)}>
                  编辑
                </Button>
                <Button size="mini" status="danger" onClick={() => remove(row.id)}>
                  删除
                </Button>
              </Space>
            ),
          },
        ]}
        data={items}
      />

      <Modal
        title={editing ? '编辑自定义 OAuth' : '添加自定义 OAuth'}
        visible={visible}
        onCancel={() => setVisible(false)}
        onOk={submit}
        style={{ width: 640 }}
      >
        <Form layout="vertical">
          <div className="grid md:grid-cols-2 gap-3">
            <Form.Item label="名称" required>
              <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            </Form.Item>
            <Form.Item label="Slug" required>
              <Input value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
            </Form.Item>
            <Form.Item label="Client ID" required>
              <Input value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} />
            </Form.Item>
            <Form.Item label="Client Secret">
              <SensitiveInput
                value={form.client_secret ?? ''}
                onChange={(v) => setForm({ ...form, client_secret: v })}
              />
            </Form.Item>
            <Form.Item label="Authorization Endpoint">
              <Input
                value={form.authorization_endpoint}
                onChange={(v) => setForm({ ...form, authorization_endpoint: v })}
              />
            </Form.Item>
            <Form.Item label="Token Endpoint">
              <Input value={form.token_endpoint} onChange={(v) => setForm({ ...form, token_endpoint: v })} />
            </Form.Item>
            <Form.Item label="User Info Endpoint">
              <Input
                value={form.user_info_endpoint}
                onChange={(v) => setForm({ ...form, user_info_endpoint: v })}
              />
            </Form.Item>
            <Form.Item label="Scopes">
              <Input value={form.scopes} onChange={(v) => setForm({ ...form, scopes: v })} />
            </Form.Item>
          </div>
          <Form.Item label="启用">
            <Switch checked={!!form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
          </Form.Item>
        </Form>
      </Modal>
    </SettingSectionCard>
  )
}
